import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  BOT_SEQUENCE_TEST_RUN_ANCHOR_FIELD,
  BOT_SEQUENCE_TEST_RUN_SUBJECT,
  BotSequenceScheduledAttemptInterface,
  BotSequenceTestRunInterface,
  BotSequenceTestRunStage,
  BotSequenceTestRunTrace,
  BotSequenceType,
  CreateTestRunBody,
  TestRunPreflight,
  TestRunPreflightStop
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Checkbox, Input, Spinner, TextArea } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { DeliveriesCell } from "../../CRM/SequenceEvents/SequenceEvents.Shared";
import { DiagnosisTone, diagnoseRun } from "./SequenceTestRun.Diagnosis";
import { buildLadderPreview } from "./SequenceTestRun.Preview";

const POLL_INTERVAL_MS = 5000;

interface PreflightGateRow {
  /** Which `stopsAt` value this row is; null = never a stop (JIT). */
  stop: TestRunPreflightStop | null;
  label: string;
  on: (preflight: TestRunPreflight) => boolean;
  value: (preflight: TestRunPreflight) => string;
}

const onOff = (on: boolean): string => (on ? "On" : "Off");

/** The gate chain in the order it applies (engine-gates.md) — the first Off is where the run stops. */
const PREFLIGHT_GATES: PreflightGateRow[] = [
  { stop: "master", label: "Sequences engine (master)", on: (p) => p.engineEnabled, value: (p) => onOff(p.engineEnabled) },
  { stop: "reconciler", label: "Reconciler", on: (p) => p.reconcilerEnabled, value: (p) => onOff(p.reconcilerEnabled) },
  { stop: "scheduler", label: "Scheduler", on: (p) => p.schedulerEnabled, value: (p) => onOff(p.schedulerEnabled) },
  { stop: "mode", label: "Location config mode", on: (p) => p.typeMode === "ENABLED", value: (p) => p.typeMode ?? "not configured" },
  { stop: "dispatcher", label: "Dispatcher", on: (p) => p.dispatcherEnabled, value: (p) => onOff(p.dispatcherEnabled) },
  { stop: null, label: "JIT lookup", on: (p) => p.jitEnabled, value: (p) => onOff(p.jitEnabled) }
];

function preflightGateNote(preflight: TestRunPreflight, gate: PreflightGateRow, index: number): string {
  const stopIndex = PREFLIGHT_GATES.findIndex((row) => row.stop !== null && row.stop === preflight.stopsAt);
  if (stopIndex !== -1 && index === stopIndex) return "the run stops here";
  if (stopIndex !== -1 && index > stopIndex) return "not reached";
  if (gate.stop === null && !gate.on(preflight))
    return preflight.jitStaticInputs ? "bypassed for this run (static FMS inputs)" : "fire proceeds on cached data";
  return "";
}

/** What each checkpoint means, in a few words — the timeline is the main diagnostic. */
const STAGE_DESCRIPTIONS: Record<BotSequenceTestRunStage, string> = {
  created: "Run registered",
  initial_diff_applied: "Fake FMS data written to the lease/lead",
  reconcile_requested: "Queued for the reconciler",
  booked: "Scheduler created the attempt",
  jit_diff_applied: "JIT refresh used the fake diff instead of the FMS",
  jit_sibling_applied: "JIT refresh used the fake sibling lead as the changes feed — the inquiry was flipped by the StorEdge sibling rule",
  send_simulated: "Send faked — attempt finalized as fired",
  completed: "Run finished",
  cancelled: "Run cancelled — the attempts it booked were cancelled with it (decision 68)"
};

const DEFAULT_CONFIG_OVERRIDE = `{
  "ladderOffsetDays": [3, 5, 7]
}`;

const EXAMPLE_SIBLING_CREATED_AT = new Date().toISOString();

/**
 * An example `jitSiblingLead` (StorEdge): a reservation recorded as a new lead, created now so it
 * is on or after the pinned inquiry. `status` / `reserved_at` are the raw keys the sibling rule reads.
 * Shown as the field's placeholder, not prefilled — any static JIT input turns the JIT gates off for the run.
 */
const EXAMPLE_JIT_SIBLING_LEAD = JSON.stringify(
  { raw: { status: "reserved", reserved_at: EXAMPLE_SIBLING_CREATED_AT }, leadCreatedDate: EXAMPLE_SIBLING_CREATED_AT },
  null,
  2
);

/**
 * A starting `initialDiff` per type: the anchor field the type reads, plus whatever else its
 * rule requires to qualify. PAST_DUE needs a delinquent payment status; MOVE_IN needs an
 * ACTIVE lease; MOVE_OUT needs neither (a move-out date on any lease is membership).
 */
function defaultInitialDiff(sequenceType: BotSequenceType): string {
  const anchorField = BOT_SEQUENCE_TEST_RUN_ANCHOR_FIELD[sequenceType];
  const extras: Record<BotSequenceType, Record<string, unknown>> = {
    [BotSequenceType.PAST_DUE]: { paymentStatus: "past due" },
    [BotSequenceType.ABANDONED_CART]: {},
    [BotSequenceType.MOVE_IN]: { active: true },
    [BotSequenceType.MOVE_OUT]: {}
  };
  return JSON.stringify({ [anchorField]: "", ...extras[sequenceType] }, null, 2);
}

const DIAGNOSIS_TONE_CLASSES: Record<DiagnosisTone, string> = {
  pass: "bg-teal-50 text-teal-900",
  pending: "bg-grey-50 text-grey-700",
  warn: "bg-yellow-50 text-yellow-900",
  fail: "bg-red-50 text-red-900"
};

function parseJsonField(raw: string, label: string): { value: Record<string, unknown> | undefined; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { value: undefined, error: null };
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return { value: undefined, error: `${label} must be a JSON object` };
    }
    return { value: parsed as Record<string, unknown>, error: null };
  } catch {
    return { value: undefined, error: `${label} is not valid JSON` };
  }
}

interface CreatedRow {
  label: string;
  id: number;
}

/**
 * The rows this run actually produced, so an operator can jump straight to them in SQL
 * instead of reverse-engineering ids from the trace. Sources differ per row: the enrollment
 * and reconcile request are stamped on the run, the attempts come from the ledger, and the
 * conversation / bot message only exist after a REAL send (a simulated one never opens them
 * — their absence is itself the confirmation that nothing went out).
 */
function collectCreatedRows(trace: BotSequenceTestRunTrace, runAttempts: BotSequenceScheduledAttemptInterface[]): CreatedRow[] {
  const rows: CreatedRow[] = [{ label: "Test run", id: trace.run.id }];

  const reconcileCheckpoint = trace.run.checkpoints.find((checkpoint) => checkpoint.stage === "reconcile_requested");
  const reconcileRequestId = reconcileCheckpoint?.detail?.reconcileRequestId;
  if (typeof reconcileRequestId === "number") {
    rows.push({ label: "Reconcile request", id: reconcileRequestId });
  }
  if (trace.run.enrollmentId !== null) {
    rows.push({ label: "Enrollment", id: trace.run.enrollmentId });
  }
  for (const attempt of runAttempts) {
    rows.push({ label: `Attempt · step ${attempt.stepOffset} · ${attempt.status}`, id: attempt.id });
  }
  for (const attempt of runAttempts) {
    if (typeof attempt.metadata?.conversationId === "number") {
      rows.push({ label: "Bot conversation", id: attempt.metadata.conversationId });
    }
    if (typeof attempt.metadata?.messageId === "number") {
      rows.push({ label: "Bot message", id: attempt.metadata.messageId });
    }
  }
  return rows;
}

/** Checkpoint evidence as compact `key=value` text — ids the stage recorded at the time. */
function formatCheckpointDetail(detail: Record<string, unknown> | undefined): string | null {
  if (!detail) {
    return null;
  }
  const parts = Object.entries(detail)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join("|") : String(value)}`);
  return parts.length > 0 ? parts.join("  ") : null;
}

interface AttemptsTableProps {
  attempts: BotSequenceScheduledAttemptInterface[];
}

function AttemptsTable({ attempts }: AttemptsTableProps): React.ReactElement {
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-grey-500">
        <tr>
          <th className="py-1">Attempt</th>
          <th>Status</th>
          <th>Step</th>
          <th>Local day</th>
          <th>Send faked</th>
          <th>Deliveries — recipient, outcome, the gate's reason</th>
        </tr>
      </thead>
      <tbody>
        {attempts.map((attempt) => (
          <tr key={attempt.id} className="border-t border-grey-100 align-top">
            <td className="py-1">#{attempt.id}</td>
            <td className="font-medium">{attempt.status}</td>
            <td>{attempt.stepOffset}</td>
            <td>{attempt.localDay}</td>
            <td>{attempt.metadata?.simulatedSend ? "yes" : ""}</td>
            <td className="py-1">
              <DeliveriesCell deliveries={attempt.metadata?.deliveries ?? null} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Full-flow sequence test runs (docs/specs/new-sequence-approach/full-flow-test-run.md):
 * drive one user through Reconciler → Scheduler → Dispatcher → Handler → JIT and watch the
 * trace, without a real FMS and without sending anything.
 *
 * Non-prod only — every action 500s against `bull-production`, which surfaces here as an
 * error banner rather than a silently dead page.
 */
export function SequenceTestRunAdminView(): React.ReactElement {
  // The org is typed, not taken from the session. These endpoints are SWIVL_ADMIN_ONLY, and
  // that scope deliberately bypasses org checks (Auth.Service) — a Swivl admin drives a run
  // against whatever org holds the subject, which is rarely the org they happen to be in.
  // Defaults to the session's org purely as a convenience.
  const sessionOrgId = Member.org?.id ?? null;
  const [orgIdInput, setOrgIdInput] = useState(sessionOrgId === null ? "" : String(sessionOrgId));
  const orgId = Number(orgIdInput) || null;

  const [sequenceType, setSequenceType] = useState<BotSequenceType>(BotSequenceType.PAST_DUE);
  const [userId, setUserId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [initialDiff, setInitialDiff] = useState(defaultInitialDiff(BotSequenceType.PAST_DUE));
  const [jitDiff, setJitDiff] = useState("");
  const [jitSiblingLead, setJitSiblingLead] = useState("");
  const [configOverride, setConfigOverride] = useState(DEFAULT_CONFIG_OVERRIDE);
  const [simulateSend, setSimulateSend] = useState(true);
  const [bypassComplianceWindow, setBypassComplianceWindow] = useState(true);
  // Outbound Gate plan decision 6: a real run is refused until the operator has read the verdict lines and ticked this.
  const [allowReal, setAllowReal] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [activeRuns, setActiveRuns] = useState<BotSequenceTestRunInterface[]>([]);
  const [preflight, setPreflight] = useState<TestRunPreflight | null>(null);
  const [runId, setRunId] = useState<number | null>(null);
  const [trace, setTrace] = useState<BotSequenceTestRunTrace | null>(null);
  const [isTicking, setIsTicking] = useState(false);
  const [isClosingEnrollment, setIsClosingEnrollment] = useState(false);

  // Every type but the cart hangs off a lease (PAST_DUE reads payment fields, MOVE_IN /
  // MOVE_OUT their move dates) — the maps are the single source of truth, shared with bull.
  const isLeaseSubject = BOT_SEQUENCE_TEST_RUN_SUBJECT[sequenceType] === "lease";
  const anchorKey = BOT_SEQUENCE_TEST_RUN_ANCHOR_FIELD[sequenceType];

  // Each type reads a different anchor field, so a diff carried over from the previous type
  // would silently never qualify. Reset the template unless it has been edited by hand.
  const handleSequenceTypeChange = (next: BotSequenceType): void => {
    if (initialDiff.trim() === defaultInitialDiff(sequenceType).trim()) {
      setInitialDiff(defaultInitialDiff(next));
    }
    // jitSiblingLead applies to lead subjects only — the API rejects it on a lease type.
    if (BOT_SEQUENCE_TEST_RUN_SUBJECT[next] === "lease") {
      setJitSiblingLead("");
    }
    setSequenceType(next);
  };

  const refreshActiveRuns = useCallback(async (): Promise<void> => {
    if (!orgId) {
      return;
    }
    try {
      setActiveRuns(await API.SequenceTestRuns.listActive(orgId));
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't load active test runs");
    }
  }, [orgId]);

  useEffect(() => {
    void refreshActiveRuns();
  }, [refreshActiveRuns]);

  // Poll the trace while the run is live. The attempts are the real signal: the run row goes
  // `completed` only on a simulated send, but a cancelled/skipped attempt is just as final.
  useEffect(() => {
    if (runId === null) {
      return;
    }
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const next = await API.SequenceTestRuns.getTrace(runId);
        if (!cancelled) {
          setTrace(next);
        }
      } catch (e) {
        if (!cancelled) {
          AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't load the run trace");
        }
      }
    };

    void tick();
    const handle = setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [runId]);

  // The trace returns every attempt for this user + type, which spans EARLIER runs too: a
  // changed anchor closes the old enrollment and opens a new one, so yesterday's rows are
  // still there. Split on the run's own enrollment — without this the verdict below could
  // read a previous run's `fired` row and claim PASS before this run has even booked.
  const runEnrollmentId = trace?.run.enrollmentId ?? null;
  const runAttempts = (trace?.attempts ?? []).filter((attempt) => runEnrollmentId !== null && attempt.enrollmentId === runEnrollmentId);
  const earlierAttempts = (trace?.attempts ?? []).filter((attempt) => runEnrollmentId === null || attempt.enrollmentId !== runEnrollmentId);

  const parsedInitial = parseJsonField(initialDiff, "initialDiff");
  const parsedJit = parseJsonField(jitDiff, "jitDiff");
  const parsedSibling = parseJsonField(jitSiblingLead, "jitSiblingLead");
  const parsedConfig = parseJsonField(configOverride, "configOverride");
  const jsonError = parsedInitial.error ?? parsedJit.error ?? parsedSibling.error ?? parsedConfig.error;

  const anchorSource = typeof parsedInitial.value?.[anchorKey] === "string" ? (parsedInitial.value[anchorKey] as string) : null;
  const ladder = Array.isArray(parsedConfig.value?.ladderOffsetDays) ? (parsedConfig.value.ladderOffsetDays as number[]) : [];
  const preview = buildLadderPreview({ sequenceType, anchorSource, ladderOffsetDays: ladder });

  // The single verdict under a live run: the outcome if an attempt reached one, otherwise
  // why nothing has booked. "Nothing due" is a silent no-op in the scheduler, so without
  // this the page would show an empty table indistinguishable from a hang.
  const diagnosis = trace ? diagnoseRun({ trace, runAttempts, ladderOffsetDays: ladder }) : null;
  const diagnosisAction = diagnosis?.action ?? null;

  const handleCreate = async (): Promise<void> => {
    if (!orgId || jsonError) {
      return;
    }
    const body: CreateTestRunBody = {
      userId: Number(userId),
      sequenceType,
      ...(isLeaseSubject ? { leaseId: Number(subjectId) } : { leadId: Number(subjectId) }),
      initialDiff: parsedInitial.value,
      jitDiff: parsedJit.value,
      jitSiblingLead: parsedSibling.value as CreateTestRunBody["jitSiblingLead"],
      configOverride: parsedConfig.value as CreateTestRunBody["configOverride"],
      simulateSend,
      bypassComplianceWindow,
      ...(!simulateSend && allowReal ? { allowReal: true } : {})
    };

    setIsCreating(true);
    setRefusal(null);
    try {
      const result = await API.SequenceTestRuns.create(orgId, body);
      setPreflight(result.preflight);
      setRunId(result.run.id);
      setTrace(null);
      await refreshActiveRuns();
      AlertsModel.successToast(`Test run ${result.run.id} created`);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Couldn't create the test run";
      // The refusal IS the verdict lines — show them where they can be read, not in a toast.
      if (message.startsWith("Refused:")) {
        setRefusal(message);
      } else {
        AlertsModel.errorToast(message);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = async (id: number): Promise<void> => {
    try {
      await API.SequenceTestRuns.cancel(id);
      if (id === runId) {
        setRunId(null);
        setTrace(null);
      }
      await refreshActiveRuns();
      AlertsModel.successToast(`Test run ${id} cancelled`);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't cancel the run");
    }
  };

  // Ends the open episode so the reconciler builds a fresh one — the standard way out of
  // "the ladder is already fully fired for this anchor". Confirmed because it closes a real
  // enrollment, even though a replacement follows within a drain tick.
  const handleCloseEnrollment = async (enrollmentId: number): Promise<void> => {
    if (!orgId || !window.confirm(`Close enrollment #${enrollmentId}? A fresh episode is created on the next reconcile.`)) {
      return;
    }
    setIsClosingEnrollment(true);
    try {
      const result = await API.SequenceTestRuns.manualUnenroll(orgId, enrollmentId);
      AlertsModel.successToast(
        result.closed ? `Enrollment #${enrollmentId} closed — waiting for the reconciler` : `Enrollment #${enrollmentId} was already closed`
      );
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't close the enrollment");
    } finally {
      setIsClosingEnrollment(false);
    }
  };

  const handleDispatchTick = async (): Promise<void> => {
    setIsTicking(true);
    try {
      await API.SequenceTestRuns.forceDispatchTick();
      AlertsModel.successToast("Dispatch tick enqueued");
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't enqueue a dispatch tick");
    } finally {
      setIsTicking(false);
    }
  };

  const canSubmit = Boolean(orgId) && userId.trim() !== "" && subjectId.trim() !== "" && !jsonError && !isCreating;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">Sequence Test Run</h1>
        <p className="text-sm text-grey-600">
          Drives one user through the whole new Sequences engine on real rows. Fakes only the FMS data, the location&apos;s sequence config, and the
          outward send. Non-production environments only.
        </p>
        <a
          href="https://app.notion.com/p/tryswivl/Test-page-in-Studio-3cf788c31d198089b747f1c7db7bd74f"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-orange-500 hover:text-orange-600"
        >
          How to use this page (Notion guide) →
        </a>
      </header>

      {activeRuns.length > 0 && (
        <section className="flex flex-col gap-2 rounded-lg border border-grey-200 p-4">
          <h2 className="text-sm font-semibold">Active runs</h2>
          {activeRuns.map((run) => (
            <div key={run.id} className="flex items-center justify-between gap-4 text-sm">
              <span>
                #{run.id} · {run.sequenceType} · user {run.userId} · {run.leaseId ? `lease ${run.leaseId}` : `lead ${run.leadId}`}
              </span>
              <div className="flex items-center gap-2">
                <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setRunId(run.id)}>
                  Watch
                </Button>
                <Button size={ButtonSize.SMALL} color={ButtonColor.RED} variant={ButtonVariant.GHOST} onClick={() => handleCancel(run.id)}>
                  Cancel
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="flex flex-col gap-4 rounded-lg border border-grey-200 p-4">
        <div className="flex flex-wrap gap-2">
          {Object.values(BotSequenceType).map((type) => (
            <Button
              key={type}
              size={ButtonSize.SMALL}
              variant={sequenceType === type ? ButtonVariant.PRIMARY : ButtonVariant.GHOST}
              onClick={() => handleSequenceTypeChange(type)}
            >
              {type}
            </Button>
          ))}
        </div>

        <div className="flex gap-4">
          <Input
            label="Org ID"
            value={orgIdInput}
            onChange={(e) => setOrgIdInput(e.target.value)}
            helperText="The org holding the subject — not necessarily the org you are signed into."
            placeholder="106"
          />
          <Input label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="118137" />
          <Input
            label={isLeaseSubject ? "Lease ID" : "Lead ID"}
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            placeholder="49107"
          />
        </div>

        <TextArea
          label="initialDiff"
          helperText={`Applied at creation through the real sync path. Set ${anchorKey} to make the user qualify.`}
          value={initialDiff}
          error={parsedInitial.error ?? undefined}
          rows={5}
          onChange={(e) => setInitialDiff(e.target.value)}
        />
        <TextArea
          label="jitDiff (optional)"
          helperText={
            isLeaseSubject
              ? "Served to the handler's JIT refresh as the FMS's answer to the by-id fetch — e.g. post a payment to force a cancel."
              : 'Served to the handler\'s JIT refresh as the FMS\'s answer to the by-id fetch of the pinned lead — what the lead looks like at send time. Only fields the sync writes take effect: "status": "inactive" (the inquiry converted or closed in place → the attempt is cancelled enrollment_closed, the expected pass for that scenario), "kind", "desiredMoveInDate". leadCreatedDate is ignored here (an FMS never changes a creation date; set it in initialDiff). For a StorEdge conversion recorded as a separate lead use jitSiblingLead instead and leave this empty.'
          }
          value={jitDiff}
          error={parsedJit.error ?? undefined}
          rows={4}
          onChange={(e) => setJitDiff(e.target.value)}
        />
        {!isLeaseSubject && (
          <TextArea
            label="jitSiblingLead (optional, StorEdge only)"
            helperText="StorEdge records a reservation or move-in as a NEW lead and never touches the inquiry; this lead is served as the location's changes feed at JIT time so the sync-time sibling rule flips the pinned inquiry. Ignored for other FMS types."
            value={jitSiblingLead}
            placeholder={EXAMPLE_JIT_SIBLING_LEAD}
            error={parsedSibling.error ?? undefined}
            rows={6}
            onChange={(e) => setJitSiblingLead(e.target.value)}
          />
        )}
        <TextArea
          label="configOverride"
          helperText="Temporary (ENG-3002): stands in for the location's missing sequence config. Without a ladder the scheduler books nothing."
          value={configOverride}
          error={parsedConfig.error ?? undefined}
          rows={4}
          onChange={(e) => setConfigOverride(e.target.value)}
        />

        {preview && (
          <div className={filterClasses("rounded-md p-3 text-sm", preview.willBook ? "bg-teal-50 text-teal-900" : "bg-yellow-50 text-yellow-900")}>
            {preview.verdict}
          </div>
        )}

        <div className="flex gap-6">
          <Checkbox label="Simulate send" checked={simulateSend} onCheckedChange={(checked) => setSimulateSend(checked === true)} />
          <Checkbox
            label="Bypass compliance window"
            checked={bypassComplianceWindow}
            onCheckedChange={(checked) => setBypassComplianceWindow(checked === true)}
          />
        </div>
        {!simulateSend && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-red-700">
              Simulate send is off — the bot builds every delivery for real and the Outbound Gate decides at the wire. Any REAL line is refused until
              you have read the lines and ticked below.
            </p>
            <Checkbox
              label="Allow REAL deliveries — I have read the verdict lines"
              checked={allowReal}
              onCheckedChange={(checked) => setAllowReal(checked === true)}
            />
          </div>
        )}

        {refusal && (
          <section className="rounded-lg border border-red-300 bg-red-50 p-4">
            <h2 className="text-sm font-semibold text-red-900">Refused — this run would make REAL deliveries</h2>
            <pre className="mt-2 font-mono text-xs whitespace-pre-wrap text-red-900">{refusal}</pre>
          </section>
        )}

        <div>
          <Button onClick={handleCreate} disabled={!canSubmit}>
            {isCreating ? <Spinner /> : "Create test run"}
          </Button>
        </div>
      </section>

      {preflight && (
        <section className="rounded-lg border border-grey-200 p-4">
          <h2 className="text-sm font-semibold">Preflight — the engine gates for this org, in the order they apply</h2>
          <p className="mt-1 text-xs text-grey-600">
            Each module runs only with the master flag. The first gate that is Off is where the run stops; gates below it are not reached.
          </p>
          <table className="mt-2 text-sm">
            <tbody>
              {PREFLIGHT_GATES.map((gate, index) => {
                const on = gate.on(preflight);
                const note = preflightGateNote(preflight, gate, index);
                return (
                  <tr key={gate.label} className={filterClasses(note === "not reached" && "text-grey-400")}>
                    <td className="py-0.5 pr-4">{gate.label}</td>
                    <td className={filterClasses("py-0.5 pr-4 font-medium", on ? "text-teal-700" : "text-red-700")}>{gate.value(preflight)}</td>
                    <td className="py-0.5 text-xs text-grey-600">{note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {preflight?.jitStaticInputs && (
        <section className="rounded-lg border border-grey-200 bg-grey-50 p-4 text-sm text-grey-700">
          JIT gates bypassed for this run (static FMS inputs)
        </section>
      )}

      {preflight && preflight.warnings.length > 0 && (
        <section className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h2 className="text-sm font-semibold text-red-900">Preflight warnings — these gates are real and are not bypassed</h2>
          <ul className="mt-2 list-disc pl-5 text-sm text-red-900">
            {preflight.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </section>
      )}

      {preflight && preflight.verdicts.length > 0 && (
        <section className="rounded-lg border border-grey-200 p-4">
          <h2 className="text-sm font-semibold">
            Outbound Gate verdicts{preflight.dueStep !== null ? ` — Day ${preflight.dueStep}` : " — whole ladder (no step due today)"}
          </h2>
          <p className="mt-1 text-xs text-grey-600">What each delivery of that day would do for this user, decided now with the real gate.</p>
          <ul className="mt-2 flex flex-col gap-1 font-mono text-xs">
            {preflight.verdicts.map((verdict) => (
              <li key={verdict.line} className={filterClasses(verdict.result === "real" ? "font-semibold text-red-700" : "text-grey-700")}>
                {verdict.line}
              </li>
            ))}
          </ul>
        </section>
      )}

      {runId !== null && (
        <section className="flex flex-col gap-4 rounded-lg border border-grey-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Run #{runId}</h2>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={handleDispatchTick} disabled={isTicking}>
              Force dispatch tick
            </Button>
          </div>

          {!trace && <Spinner />}

          {trace && (
            <>
              <ol className="flex flex-col gap-1 text-sm">
                {trace.run.checkpoints.map((checkpoint) => {
                  const detail = formatCheckpointDetail(checkpoint.detail);
                  return (
                    <li key={`${checkpoint.stage}-${checkpoint.at}`} className="flex gap-3">
                      <span className="font-mono text-grey-500">{new Date(checkpoint.at).toLocaleTimeString()}</span>
                      <span className="w-48 font-medium">{checkpoint.stage}</span>
                      <span className="text-grey-600">{STAGE_DESCRIPTIONS[checkpoint.stage]}</span>
                      {detail && <span className="font-mono text-xs text-grey-400">{detail}</span>}
                    </li>
                  );
                })}
              </ol>

              <div className="flex flex-col gap-1 rounded-md bg-grey-50 p-3">
                <span className="text-xs font-semibold tracking-wide text-grey-500 uppercase">Rows created by this run</span>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {collectCreatedRows(trace, runAttempts).map((row) => (
                    <span key={`${row.label}-${row.id}`} className="text-grey-700">
                      {row.label} <span className="font-mono font-medium">#{row.id}</span>
                    </span>
                  ))}
                </div>
                <span className="text-xs text-grey-500">
                  {trace.reconcileRuns.length} reconcile run(s) for this user · subject{" "}
                  {trace.run.leaseId ? `lease #${trace.run.leaseId}` : `lead #${trace.run.leadId}`} (pre-existing, not created here)
                </span>
              </div>

              {runAttempts.length > 0 && <AttemptsTable attempts={runAttempts} />}

              {earlierAttempts.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-grey-600">{earlierAttempts.length} attempt(s) from earlier runs on this user</summary>
                  <p className="mt-1 mb-2 text-grey-500">
                    A changed anchor closes the old enrollment and opens a new one, so previous episodes stay visible here.
                  </p>
                  <AttemptsTable attempts={earlierAttempts} />
                </details>
              )}

              {diagnosis && (
                <div className={filterClasses("flex flex-col gap-1 rounded-md p-3 text-sm", DIAGNOSIS_TONE_CLASSES[diagnosis.tone])}>
                  <span className="font-medium">{diagnosis.headline}</span>
                  {diagnosis.detail && <span>{diagnosis.detail}</span>}
                  {diagnosis.nextStep && <span className="font-medium">→ {diagnosis.nextStep}</span>}
                  {diagnosisAction && (
                    <div className="mt-2">
                      <Button
                        size={ButtonSize.SMALL}
                        variant={ButtonVariant.GHOST}
                        disabled={isClosingEnrollment}
                        onClick={() => handleCloseEnrollment(diagnosisAction.enrollmentId)}
                      >
                        {diagnosisAction.label}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
